/**
 * Phase 24 Plan 24-03 — internal-surface board API client.
 *
 * USED BY: internal route /projects/:id/board (audience: 'internal').
 * NOT used directly by the external route — see externalBoard.ts for that.
 *
 * Mutation endpoints (PATCH state, PATCH visibility) are defined here and
 * shared by both audiences (the backend rejects external attempts with
 * Phase 22 locked codes EXTERNAL_FORBIDDEN_STATE_CHANGE /
 * EXTERNAL_FORBIDDEN_VISIBILITY_FLIP — the client doesn't re-derive
 * permission, it just relays).
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import { publishReleaseFlagDeferral, type DoneDeferral } from '../releaseFlagDeferral';

// MKS-163 — one enum tuple, three consumers (backend HR-2): this union's
// four values mirror backend/src/api/schemas/taskMutation.ts TASK_PRIORITIES,
// which mirrors the `tasks_priority_chk` CHECK constraint. Deliberately NOT
// widened with `| string` (unlike MyTaskDto['priority']) so TaskPriorityPicker
// gets real exhaustiveness on its option list.
export type TaskPriority = 'urgent' | 'high' | 'normal' | 'low';

// MKS-164 — a task label (global or project-scoped), as carried on the
// shared task-card DTO. Mirrors backend/src/services/taskLabelService.ts's
// TaskLabelDto minus `projectId` — the card only needs id/name/color to
// render a chip.
export interface TaskLabel {
  id: number;
  name: string;
  color: string;
}

/**
 * MKS-292 — one counterpart of a block relation on a task card. Mirrors the
 * backend `TaskBlockRefDto` (`taskBlockRelationService.ts`), merged per
 * counterpart task: `areaLabels` is non-empty when an MKS-241 area overlap
 * produced the relation, `link` is true when an explicit `blocks` task link
 * did. Both can be true for the same pair.
 */
export interface TaskBlockRef {
  taskId: number;
  /** `tasks.code`; null on the legacy rows that never got one. */
  code: string | null;
  areaLabels: string[];
  link: boolean;
}

/**
 * MKS-312 (Plan 126-05 / D-19) — one child or manual row of a task's active Factory family. Kept in
 * lockstep with the backend `FactoryFamilyCardRow`.
 */
export interface FactoryFamilyRow {
  repository: string | null;
  role: 'child' | 'manual';
  identifier: string | null;
  title: string | null;
  status: string | null;
  runStatus: string | null;
  prNumber: number | null;
  /** https://github.com/... for an own-organisation repository, else null. */
  prUrl: string | null;
  mergedAt: string | null;
  deployedAt: string | null;
  allowlisted: boolean;
  issueUrl: string | null;
}

export interface TaskCard {
  id: number;
  name: string;
  code?: string | null;
  description: string | null;
  /**
   * Phase 51 INTAKE-02 (CF-2) — relaxed to number | null.
   * Triage cards (status='triage') have taskStateId=null because they are not
   * assigned to any task state column. Real-state cards retain their integer id.
   * Kept in lockstep with the backend DTO and board.ts response schema.
   */
  taskStateId: number | null;
  isExternal: boolean;
  createdAt: string;
  // 260723 — last-activity timestamp (task edit / comment / time entry); drives
  // the staleness emoji (💩 >5d, 🪦 >15d with no activity). Server always sends
  // it; optional on the wire type so legacy test fixtures need not set it.
  lastActivityAt?: string;
  projectId: number;
  milestoneId: number | null; // Phase 30 D-02 — server emits in BOTH properties AND required
  /**
   * MKS-136 — the OPTIONAL billing period this ticket is tied to ("must be
   * delivered for payment 2"). Independent of `milestoneId` above: a ticket
   * sits in a sprint AND may be tied to a payment at the same time.
   */
  billingPeriodId?: number | null;
  /**
   * MKS-341 — the ticket's optional due date as a CALENDAR DAY (`YYYY-MM-DD`),
   * never an ISO instant: the server emits the `@db.Date` column verbatim so
   * "overdue" cannot flip with the reader's timezone. `null` when nobody set
   * one, which is the default for every ticket.
   *
   * Deliberately NOT the same thing as `MyTaskDto.dueAt`, which is a full ISO
   * instant AND falls back to the milestone's end date. This value is the
   * ticket's own date only — see `taskCardSerializer.ts`'s `dueDate` comment on
   * why the card's amber/red state must never see that fallback.
   *
   * Optional here only so legacy test fixtures (built before this change) keep
   * compiling, matching the priority/labels precedent below — the server
   * always emits it (in required[] on taskCardSchema).
   */
  dueDate?: string | null;
  /**
   * MKS-364 — true while the ticket is CLOSED for time booking: no time entry
   * may be created on it, and no existing entry may be moved onto it. Flipped
   * by an admin or the project's PM.
   *
   * Optional here only so legacy test fixtures (built before this change) keep
   * compiling, matching the `dueDate` / `billingPeriodId` precedent above — the
   * server always emits it (it is in `required[]` on `taskCardSchema`). Read it
   * as `task.timeBookingLocked === true`, never as truthiness of a possibly
   * absent key, so a fixture that omits it reads as "open" rather than as
   * "unknown".
   */
  timeBookingLocked?: boolean;
  budget: number | null;
  hardLimit: boolean;
  plannedHours: number | null;
  reportedHours: number;
  canFlipVisibility: boolean;
  /**
   * Petra UAT 2026-05-19 — assignee surface for the board avatar.
   * Server-side join: tasks.assignee_id → users.first_name + last_name.
   * Both null when the task is unassigned (free-market backlog per D-03).
   * Declared optional on the wire type so legacy test fixtures (created
   * before Phase 45 Wave 5 added these fields) and graceful-degradation
   * call sites do not need to be touched. The Avatar renders only when
   * `task.assigneeName` is truthy, so undefined is harmless at runtime.
   */
  assigneeId?: number | null;
  /**
   * External tasks v1 (260702) — the external assignee id (external_users.id),
   * null when the task is unassigned or assigned to an internal user. Lets the
   * external-audience slide-over gate bod 2 / bod 3 on
   * `externalAssigneeId === currentExternalUser.id`. Optional on the wire type
   * for legacy fixtures; always emitted by the server (in required[]).
   */
  externalAssigneeId?: number | null;
  assigneeName?: string | null;
  /**
   * Phase 66 (EAP-05) — discriminates the assignee kind so consumers can branch
   * rendering (external badge, tabbed picker active tab, etc.).
   * 'user' — internal staff member assigned.
   * 'external_user' — external user assigned.
   * null — task is unassigned.
   * Optional on the wire type for legacy test fixtures (mirrors assigneeId/Name).
   * Always emitted by the server (declared in required in the board response schema).
   */
  assigneeKind?: 'user' | 'external_user' | null;
  /**
   * Quick 260915-i0x — the assignee's avatar color slot
   * (`users.avatar_color_seq`), fed straight to `<Avatar colorSeq>`.
   * Null for external assignees (external_users has no slot) and unassigned
   * tasks; Avatar then falls back to hashing the name.
   */
  assigneeColorSeq?: number | null;
  /**
   * MKS-203 — relative API path to the assignee's effective avatar, or null
   * for external assignees/unassigned tasks. Same rule as assigneeColorSeq
   * above.
   */
  assigneeAvatarUrl?: string | null;
  /**
   * Phase 49 — tier badge on kanban cards + TaskDetailSlideOver picker.
   * Server-side join: tasks.tier_id → project_sla_tiers (id, order_index, name).
   * Null when:
   *   - task is on a non-SLA Task project (project.taskTypeId !== SLA_TASK_TYPE_ID), OR
   *   - task pre-dates the Phase 49 tier write path.
   * Optional on the wire type for legacy fixtures (mirrors assigneeId/Name).
   * Color is derived client-side from orderIndex via getTierColor() — see Plan 49-02.
   */
  tier?: { id: number; orderIndex: number; name: string } | null;
  /**
   * 260722-g8r — creator attribution for the slide-over "Created by" row.
   * Resolution mirrors assigneeName/assigneeKind (external-first). Null on
   * legacy tasks created before the creator stamp. Optional on the wire type
   * for legacy test fixtures; always emitted by the server (in required[]).
   */
  createdByName?: string | null;
  createdByKind?: 'user' | 'external_user' | null;
  /**
   * Quick task 260810-l18 — raw creator ids, so the portal + slide-over can
   * gate the author-only Approve/Reopen panel on
   * `createdById === currentUser.id` / `createdByExternalId ===
   * currentExternalUser.id` without a lookup. Optional on the wire type for
   * legacy test fixtures; always emitted by the server (in required[]).
   */
  createdById?: number | null;
  createdByExternalId?: number | null;
  /**
   * MKS-163 — task priority ('urgent' | 'high' | 'normal' | 'low'). Server
   * NOT NULL DEFAULT 'normal', always emitted (in required[] on
   * taskCardSchema); optional here only so legacy test fixtures (built
   * before this change) keep compiling, matching the assigneeId/
   * lastActivityAt precedent.
   */
  priority?: TaskPriority;
  /**
   * MKS-164 — task labels. Server ALWAYS emits an array (in required[] on
   * taskCardSchema, empty when the task has none); optional here only so
   * legacy test fixtures (built before this change) keep compiling,
   * matching the priority/assigneeId precedent above.
   */
  labels?: TaskLabel[];
  /**
   * Phase 113 (Plan 113-04 / PCSYNC-03) — Factory™ dispatch state:
   * 'pending' | 'dispatched' | 'dispatch_failed', or null when the task was
   * never routed to Factory™. Optional on the wire type only so legacy test
   * fixtures (built before this change) keep compiling, matching the
   * priority/labels precedent above — the server always emits it (in
   * required[] on taskCardSchema).
   */
  paperclipDispatchStatus?: string | null;
  /**
   * Phase 114 (Plan 114-04 / PCEXEC-01) — Factory™ execution run-status:
   * 'running' | 'finished' | 'failed' | 'cancelled', or null when the task
   * has never been run by Factory™. A SIBLING of paperclipDispatchStatus
   * (D-114-05), never a replacement — the two facts can legitimately
   * disagree (dispatched but never started). Optional on the wire type only
   * so legacy test fixtures (built before this change) keep compiling —
   * the server always emits it (in required[] on taskCardSchema).
   */
  paperclipRunStatus?: string | null;
  /**
   * Phase 115 (Plan 115-03 / PCOPS-05, D-115-13) — the outbound link to
   * this task's Factory™ issue (dispatch URL's origin + the raw issue id),
   * or null when the task has no Factory™ issue yet or the dispatch URL is
   * unset. Optional on the wire type only so legacy test fixtures (built
   * before this change) keep compiling — the server always emits it (in
   * required[] on taskCardSchema).
   */
  paperclipIssueUrl?: string | null;
  /**
   * True when the dispatch is `pending` only because the project has no
   * Factory™ binding — it will not move until an admin binds the project
   * and retries. Derived server-side, never stored.
   */
  paperclipAwaitingBinding?: boolean;
  /**
   * Phase 130 (Plan 130-06 / D-15) — why a Factory™ dispatch is held back for this task:
   * 'runner_building' (the project's runner is still building) or 'runner_failed' (it could not be
   * built), or null when nothing holds it. Optional on the wire type only so legacy test fixtures
   * keep compiling — the server always emits it (in required[] on taskCardSchema).
   */
  paperclipHoldReason?: string | null;
  /**
   * MKS-292 — the open tasks that block this one, in ONE entry per counterpart
   * merged across both reasons a task can be blocked: an explicit `blocks` task
   * link (MKS-162) and an MKS-241 Robot area hold. The server always emits the
   * array (empty when nothing blocks the task, and always empty for the
   * external audience and while the release flag is off); optional on the wire
   * type only so legacy test fixtures keep compiling, matching the
   * priority/labels precedent above.
   */
  blockedBy?: TaskBlockRef[];
  /** MKS-292 — the mirror of `blockedBy`: the open tasks this one blocks. */
  blocks?: TaskBlockRef[];
  /**
   * MKS-312 (Plan 126-05 / D-19) — the active Factory family's child and manual rows. The server
   * always emits the array ([] for single-repository tickets and for the external audience);
   * optional on the wire type only so legacy test fixtures keep compiling.
   */
  factoryFamily?: FactoryFamilyRow[];
}

export interface BoardColumn {
  /**
   * Phase 51 INTAKE-02 (CF-2) — relaxed to number | null.
   * The synthetic Triage column has taskStateId=null (no real task_state row).
   * Use isTriage to discriminate; do not use taskStateId as a sentinel.
   */
  taskStateId: number | null;
  name: string;
  ordering: number;
  color: string;
  /**
   * Phase 51 INTAKE-02 (CF-2) — explicit discriminator flag.
   * true for the synthetic leading Triage column; false for all real state columns.
   * Always emitted by the server (declared in required in the board response schema).
   */
  isTriage: boolean;
  /**
   * 260723-tst — terminal flag mirrored from task_states.is_terminal. The
   * server always emits it (declared in required in the board response schema);
   * optional on the wire type only so legacy test fixtures need not set it
   * (mirrors the assigneeName/createdByName precedent). Drives the age 💩 emoji
   * suppression + the column-header terminal icon. Triage = false.
   */
  isTerminal?: boolean;
  /**
   * 260726-wip — "work in progress" flag mirrored from task_states.is_in_progress.
   * The server always emits it (declared in required in the board response
   * schema); optional on the wire type only so legacy test fixtures need not
   * set it. Drives the column-header WIP badge. Triage = false.
   */
  isInProgress?: boolean;
  /**
   * MKS-154 — raw task_states.sla_timer_effect ('running' | 'response_stopped'
   * | 'resolved' | 'waiting' | 'terminal'), null for the synthetic Triage
   * column. Server always emits it; optional on the wire type only so legacy
   * test fixtures need not set it (mirrors isTerminal/isInProgress).
   */
  slaTimerEffect?: string | null;
  /**
   * MKS-154 — true iff this column's slaTimerEffect is the value the backend
   * terminal-no-return guards compare against (currently 'terminal').
   * Independent of isTerminal, which is a separate DB column. Server always
   * emits it; optional on the wire type only so legacy test fixtures need not
   * set it.
   */
  isLocked?: boolean;
  /**
   * MKS-419 — merged board only: every project that contributes a state to
   * this column (parent first). Absent outside the merged view. A drop of a
   * subproject card onto a column whose list excludes that project is refused
   * client-side.
   */
  projectIds?: number[];
  tasks: TaskCard[];
}

/** MKS-419 — one readable descendant on a merged board. */
export interface BoardSubproject {
  id: number;
  slug: string;
  name: string | null;
  /** The project's own parent: the board's project, or another subproject. */
  parentId?: number | null;
}

export interface BoardResponse {
  columns: BoardColumn[];
  /** MKS-419 — present only on a merged board (`includeSubprojects`). */
  subprojects?: BoardSubproject[];
}

export async function fetchBoardInternal(
  projectId: string,
  opts?: { includeSubprojects?: boolean },
): Promise<BoardResponse> {
  // MKS-419 — the querystring is added ONLY when asked for, so today's request
  // is byte-identical while the view is off.
  const qs = opts?.includeSubprojects ? '?includeSubprojects=true' : '';
  const res = await apiGet<{ data: BoardResponse }>(`/projects/${projectId}/board${qs}`);
  return res.data;
}

interface TaskStateMoveResult {
  id: number;
  taskStateId: number;
  isExternal: boolean;
  deferred?: DoneDeferral | null;
}

export async function patchTaskState(
  taskId: number,
  toStateId: number,
): Promise<TaskStateMoveResult> {
  const res = await apiPatch<{ data: TaskStateMoveResult }>(`/tasks/${taskId}/state`, {
    toStateId,
  });
  // Phase 120 (D-120-23): a deferred Done move answers 200 and the task did not
  // move — tell the person who clicked (board drag, status picker, bulk close).
  if (res.data.deferred) publishReleaseFlagDeferral(res.data.deferred);
  return res.data;
}

/**
 * MKS-419 — move a task to the column with this NAME in the task's OWN project
 * (the merged board's drag of a subproject card). The server resolves the name
 * after the normal authorization and answers 400 TARGET_STATE_NAME_NOT_FOUND
 * when the project has no such column. Same Done-deferral handling as above.
 */
export async function patchTaskStateByName(
  taskId: number,
  toStateName: string,
): Promise<TaskStateMoveResult> {
  const res = await apiPatch<{ data: TaskStateMoveResult }>(`/tasks/${taskId}/state`, {
    toStateName,
  });
  if (res.data.deferred) publishReleaseFlagDeferral(res.data.deferred);
  return res.data;
}

export async function patchTaskVisibility(
  taskId: number,
  isExternal: boolean,
): Promise<{ id: number; isExternal: boolean }> {
  const res = await apiPatch<{ data: { id: number; isExternal: boolean } }>(
    `/tasks/${taskId}/visibility`,
    { isExternal },
  );
  return res.data;
}

/**
 * Phase 30 — PATCH /api/tasks/:id/milestone. Pass `milestoneId: null` to unassign.
 * 422 with code 'milestone_project_mismatch' on cross-project assignment.
 */
export async function patchTaskMilestone(
  taskId: number,
  milestoneId: number | null,
): Promise<{ id: number; milestoneId: number | null }> {
  const res = await apiPatch<{ data: { id: number; milestoneId: number | null } }>(
    `/tasks/${taskId}/milestone`,
    { milestoneId },
  );
  return res.data;
}

/**
 * MKS-364 — the machine-readable code on the refusal a locked ticket produces,
 * so a caller that wants to branch on it (rather than just show the message)
 * matches on this instead of on the copy. Mirrors
 * `PROJECT_MONTH_LOCKED_CODE` in `projectMonthLocks.ts` — a small duplicate of
 * the backend constant, which is this codebase's established shape for a shared
 * rejection code (the two projects do not import across each other).
 */
export const TASK_TIME_BOOKING_LOCKED_CODE = 'TASK_TIME_BOOKING_LOCKED';

/**
 * MKS-364 — PATCH /api/tasks/:id/time-booking-lock. Admin or the project's PM
 * only; the same gate guards both directions.
 */
export async function setTaskTimeBookingLock(
  taskId: number,
  locked: boolean,
): Promise<{ id: number; timeBookingLocked: boolean }> {
  const res = await apiPatch<{ data: { id: number; timeBookingLocked: boolean } }>(
    `/tasks/${taskId}/time-booking-lock`,
    { locked },
  );
  return res.data;
}

/**
 * Phase 49 — PATCH /api/tasks/:taskId/tier. Server endpoint exists since
 * Phase 38 (taskTierRoutes.ts). The signature accepts ONLY non-null
 * tierId in Phase 49 — "Clear tier" is deferred per CONTEXT Decision E,
 * so null is reserved for future use and intentionally rejected at the
 * type layer. The backend additionally enforces the gate on SLA tasks
 * (Plan 49-01) and returns 422 with code 'task_tier_required' on null.
 * Restricting the parameter here keeps the API-client contract symmetric
 * with the `useUpdateTaskTier` hook (UpdateTaskTierInput.tierId is number).
 *
 * Response shape: `{ data: { taskId, tierId } }` — confirmed against
 * backend/src/api/schemas/taskTier.ts (taskTierUpdateResponse). The field
 * is `taskId` (not `id`) per the schema's required array.
 */
export async function patchTaskTier(
  taskId: number,
  tierId: number,
): Promise<{ taskId: number; tierId: number | null }> {
  const res = await apiPatch<{ data: { taskId: number; tierId: number | null } }>(
    `/tasks/${taskId}/tier`,
    { tierId },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase 24 Plan 24-08 — internal task CRUD wire types and functions.
//
// Backend wire contract (Plan 24-07, backend/src/api/schemas/taskMutation.ts):
//   POST   /api/projects/:projectId/tasks  →  taskMutationResponseSchema
//   PATCH  /api/tasks/:id                  →  taskMutationResponseSchema
//   DELETE /api/tasks/:id                  →  taskDeleteResponseSchema
//
// Properties NOT enumerated in those response schemas are silently stripped
// by fast-json-stringify (CLAUDE.md MEMORY). Keep TaskCrudResponse in lockstep
// with the schema field list.
// ---------------------------------------------------------------------------
export interface TaskCrudResponse {
  id: number;
  name: string;
  code?: string | null;
  description: string | null;
  taskStateId: number;
  isExternal: boolean;
  projectId: number;
  budget: number | null;
  hardLimit: boolean;
  plannedHours: number | null;
  milestoneId: number | null; // Phase 31 D-12 — keep in lockstep with taskMutationResponseSchema
  tierId: number | null; // Phase 49 Plan 49-05 — keep in lockstep with taskMutationResponseSchema (Plan 49-01 extended it)
  dueDate?: string | null; // MKS-341 — YYYY-MM-DD | null; optional so pre-MKS-341 fixtures keep compiling
  createdAt: string;
  updatedAt: string;
}

/**
 * POST /api/projects/:projectId/tasks (internal-mode body).
 * Backend (Plan 24-07) dispatches by actor.kind; internal actors land on
 * createInternalTask which forces is_external=false and requires admin OR
 * PM OR non-deleted ProjectAssignment. Body MUST NOT include is_external —
 * server rejects with 403 EXTERNAL_FORBIDDEN_INTERNAL_ONLY otherwise.
 */
export async function createTask(
  projectId: string,
  input: {
    taskStateId: number;
    name: string;
    description?: string | null;
    budget?: number | null;
    hardLimit?: boolean;
    plannedHours?: number | null;
    milestoneId?: number | null; // Phase 31 D-12 — per-cell create lands task in the right swimlane row
    tierId?: number | null; // Phase 49 Plan 49-05 — RC-01 gate: required for SLA Task projects
    labelIds?: number[]; // MKS-202 — labels picked on the create form, applied inside the create tx
  },
): Promise<TaskCrudResponse> {
  const res = await apiPost<{ data: TaskCrudResponse }>(
    `/projects/${projectId}/tasks`,
    input,
  );
  return res.data;
}

/**
 * PATCH /api/tasks/:id — name/description edit. Internal-only.
 * External actors get 403 EXTERNAL_FORBIDDEN_TASK_EDIT (Plan 24-07).
 */
export async function updateTask(
  taskId: number,
  input: {
    name?: string;
    description?: string | null;
    budget?: number | null;
    hardLimit?: boolean;
    plannedHours?: number | null;
    priority?: TaskPriority; // MKS-163
    // MKS-341 — `null` CLEARS the due date; omit the key to leave it alone.
    dueDate?: string | null;
  },
): Promise<TaskCrudResponse> {
  const res = await apiPatch<{ data: TaskCrudResponse }>(
    `/tasks/${taskId}`,
    input,
  );
  return res.data;
}

/**
 * DELETE /api/tasks/:id — hard delete. Pre-delete payload captured in
 * task_lifecycle audit before the row is removed. Internal-only.
 */
export async function deleteTask(
  taskId: number,
): Promise<{ deleted: boolean; taskId: number }> {
  const res = await apiDelete<{ data: { deleted: boolean; taskId: number } }>(
    `/tasks/${taskId}`,
  );
  return res.data;
}

/**
 * POST /api/tasks/:id/archive — quick task 260825-ik4 (D-1). Soft archive:
 * task disappears from the board but keeps its title reserved. Internal-only.
 */
export async function archiveTask(
  taskId: number,
): Promise<{ archived: boolean; taskId: number }> {
  const res = await apiPost<{ data: { archived: boolean; taskId: number } }>(
    `/tasks/${taskId}/archive`,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase 25 Plan 25-07 EXT-01 — external task-create wire wrapper.
//
// Backend wire contract (Phase 22 D-22, taskMutationRoutes):
//   POST /api/projects/:projectId/tasks chained authenticateAny +
//   requireExternalActor. Server forces is_external=true AND
//   taskStateId = first state in project's task_states.ordering. The body
//   intentionally omits taskStateId so the server-side default stays the
//   single source of truth. Body MUST NOT include is_external — server
//   rejects pre-Zod with 403 EXTERNAL_FORBIDDEN_INTERNAL_ONLY otherwise.
//
// Response shape: same taskMutationResponseSchema as the internal createTask;
// fast-json-stringify silently strips properties not enumerated in the
// schema, so ExternalCreateTaskResponse is kept in lockstep with the
// known fields (CLAUDE.md MEMORY).
// ---------------------------------------------------------------------------
export interface ExternalCreateTaskInput {
  name: string;
  description?: string | null;
  /**
   * MKS-339 — the optional helpdesk request category the client picked.
   * Omitted (or null) when the project has no categories or the client left
   * the field empty; the server validates the id against the project's own
   * non-archived list and refuses anything else with a 422.
   */
  categoryId?: number | null;
}

interface ExternalCreateTaskResponse {
  data: {
    id: number;
    name: string;
    description: string | null;
    taskStateId: number | null;
    isExternal: boolean;
    projectId: number;
  };
}

/**
 * POST /api/projects/:projectId/tasks for an external session.
 * No taskStateId in the body — every external create on an intake-enabled
 * project is routed to Triage server-side (Phase 51 UAT #3); on non-intake
 * projects the server picks the first state.
 */
export async function createExternalTask(
  projectId: string,
  input: ExternalCreateTaskInput,
): Promise<ExternalCreateTaskResponse['data']> {
  const res = await apiPost<ExternalCreateTaskResponse>(`/projects/${projectId}/tasks`, {
    name: input.name,
    description: input.description ?? null,
    // MKS-339 — only sent when the client actually picked one. The create
    // body is `.strict()` on the server, so an always-present key would be
    // fine, but omitting it keeps the request identical to today's for every
    // project that has no categories.
    ...(input.categoryId != null ? { categoryId: input.categoryId } : {}),
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase 51 Plan 51-07 INTAKE-02 — move a triage task to the first open state.
//
// Backend endpoint (Plan 06): POST /api/tasks/:taskId/intake/move-to-open
// Requires: task.assigneeId set; task.tierId set on SLA projects (else 422).
// Response: { data: { id, taskStateId, status } }
// ---------------------------------------------------------------------------

interface MoveTaskToOpenResponse {
  data: {
    id: number;
    taskStateId: number | null;
    status: string | null;
  };
}

/**
 * POST /api/tasks/:taskId/intake/move-to-open
 * Moves a task from triage to the first real task_state (assignee + tier required).
 */
export async function moveTaskToOpen(
  taskId: number,
): Promise<MoveTaskToOpenResponse['data']> {
  const res = await apiPost<MoveTaskToOpenResponse>(
    `/tasks/${taskId}/intake/move-to-open`,
    {},
  );
  return res.data;
}
